// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import React, { ChangeEvent, Component } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.onePageCheckout;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const { fulfillmentMethod } = state.context.session;
    return {
        notes: cart?.notes || state.xCheckout.orderNotes,
        fulfillmentMethod,
    };
};

const mapDispatchToProps = {
    setNote: (orderNotes: string) => ({
        type: "xCheckout/SetNote",
        data: { orderNotes },
    }),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & TextAreaProps;

export interface CheckoutShippingOrderNotesEntryStyles {
    wrapper?: InjectableCss;
    textArea?: TextAreaProps;
}

export const checkoutShippingOrderNotesEntryStyles: CheckoutShippingOrderNotesEntryStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-top: 24px;
            width: 100%;
        `,
    },
    textArea: {
        css: css`
            min-width: 100%;
            max-width: 100%;
            min-height: 50px;
            input {
                resize: vertical;
            }
        `,
    },
};

const styles = checkoutShippingOrderNotesEntryStyles;

interface State {
    notes?: string;
    showNotes?: boolean;
}

class OnePageCheckoutOrderNotesEntry extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            notes: this.props.notes,
            showNotes: !!this.props.notes,
        };
    }

    notesChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            notes: event.currentTarget.value,
        });
        this.props.setNote(event.currentTarget.value);
    };

    static getDerivedStateFromProps(props: Props, state: State) {
        if (typeof state.notes === "undefined" && typeof props.notes !== "undefined") {
            return {
                notes: props.notes,
            };
        }
        return state;
    }

    lineNotesClickHandler = () => {};

    render() {
        // if (typeof this.state.notes === "undefined") {
        //     return null;
        // }
        return (
            <TextArea
                {...styles.textArea}
                label={
                    this.props.fulfillmentMethod === FulfillmentMethod.Ship
                        ? siteMessage("Custom_Checkout_Note_Label_Ship")
                        : siteMessage("Custom_Checkout_Note_Label_Pickup")
                }
                placeholder={translate("Please add any additional notes or requirements here...")}
                value={this.state.notes}
                onChange={this.notesChangeHandler}
                {...{ isOptional: true }}
                // XNG-Change
                data-test-selector="order_note_text_area"
            />
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutOrderNotesEntry),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        displayName: "Order Notes Entry",
        allowedContexts: ["OnePageCheckoutPage"],
    },
};

export default widgetModule;
